import { useAriaAvatar } from '@wix/aria-ai-avatar';
import { Box, Button, Text } from '@wix/design-system';
import {
  useEclipsePlanRecommendation,
  useSelectButton,
} from '@wix/package-picker-shared-eclipse';
import { Confirm } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import { DATA_HOOKS } from './constants';
import { CloseIcon, SparkleIcon } from './AiPlanRecommendationCard.icons';
import { useTypingAnimation } from './useTypingAnimation';
import s from './AiPlanRecommendationCard.scss';

export const AiPlanRecommendationCard: React.FC = () => {
  const { t } = EclipsePackagePickerModule.internals.useTranslations();
  const { data, isLoading, isError, isEnabled, recommendedProductName } =
    useEclipsePlanRecommendation();

  const [initialState] = React.useState<'analyzing' | 'expanded' | 'collapsed'>(
    () => {
      const store =
        EclipsePackagePickerModule.SDK.aiRecommendationStore.getStore();
      if (store.isCollapsed) {
        return 'collapsed';
      }
      if (store.isRevealed) {
        return 'expanded';
      }
      return 'analyzing';
    },
  );
  const subtitle = data?.subtitle ?? '';
  const bullets = data?.bullets ?? [];
  const aiText = [
    t('premium-purchase-plan.eclipse.ai-recommendation.insight-label'),
    t(
      'premium-purchase-plan.eclipse.ai-recommendation.recommended-plan-heading',
    ),
    subtitle,
    ...bullets,
    t('premium-purchase-plan.eclipse.ai-recommendation.disclaimer'),
  ]
    .filter(Boolean)
    .join(' | ');

  const reportAiEvent = React.useCallback(
    (
      action: 'load' | 'view' | 'click',
      clickType?: 'chip' | 'close' | 'select',
    ) => {
      const report =
        EclipsePackagePickerModule.internals.bi?.onAiRecommendationComponent;
      if (!report) {
        return false;
      }
      report({
        action,
        clickType,
        aiText,
        recommendation: recommendedProductName,
        productId: data?.recommendedProductId,
      });
      return true;
    },
    [aiText, recommendedProductName, data],
  );

  const { SelectButton } = useSelectButton({
    productId: data?.recommendedProductId ?? '',
    label: t('premium-purchase-plan.eclipse.ai-recommendation.select-plan', {
      planName: recommendedProductName,
    }),
    selectClickType: 'ai recommendation component',
    onSelectClick: () => reportAiEvent('click', 'select'),
  });
  const { AriaAvatar, lookAt } = useAriaAvatar();

  const lookAtRef = React.useRef(lookAt);
  lookAtRef.current = lookAt;

  const { phase, typedSubtitle, visibleBulletCount, expand, collapse } =
    useTypingAnimation({
      subtitle,
      bulletCount: bullets.length,
      isDataReady: !isLoading && !!data,
      initialState,
    });

  const handleExpand = React.useCallback(() => {
    reportAiEvent('click', 'chip');
    expand();
  }, [reportAiEvent, expand]);

  const handleCollapse = React.useCallback(() => {
    reportAiEvent('click', 'close');
    collapse();
  }, [reportAiEvent, collapse]);

  const isDeadEnd = isEnabled && !isLoading && !data;
  React.useEffect(() => {
    const cardExpanding =
      phase === 'typing-subtitle' ||
      phase === 'revealing-bullets' ||
      phase === 'expanded-complete' ||
      phase === 'collapsed';
    if (cardExpanding || isDeadEnd) {
      EclipsePackagePickerModule.SDK.aiRecommendationStore.reveal();
    }
  }, [phase, isDeadEnd]);

  const hasLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (data && !hasLoadedRef.current && reportAiEvent('load')) {
      hasLoadedRef.current = true;
    }
  }, [data, phase, reportAiEvent]);

  const hasViewedRef = React.useRef(false);
  React.useEffect(() => {
    const isRevealed =
      phase === 'typing-subtitle' ||
      phase === 'revealing-bullets' ||
      phase === 'expanded-complete';
    if (isRevealed && !hasViewedRef.current && reportAiEvent('view')) {
      hasViewedRef.current = true;
    }
  }, [phase, reportAiEvent]);

  React.useEffect(() => {
    let raf = 0;
    let cx = 0;
    let cy = 0;
    const onPointerMove = (e: PointerEvent) => {
      cx = e.clientX;
      cy = e.clientY;
      if (raf) {
        return;
      }
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        lookAtRef.current(cx, cy);
      });
    };
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      if (raf) {
        window.cancelAnimationFrame(raf);
      }
    };
  }, []);

  const [portalNode, setPortalNode] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    setPortalNode(document.body);
  }, []);

  if (isError || !isEnabled) {
    return null;
  }

  if (!isLoading && !data) {
    return null;
  }

  const isCardVisible =
    phase === 'typing-subtitle' ||
    phase === 'revealing-bullets' ||
    phase === 'expanded-complete';

  const isTypingSubtitle = phase === 'typing-subtitle';
  const isComplete = phase === 'expanded-complete';

  const ariaLabel = t(
    'premium-purchase-plan.eclipse.ai-recommendation.aria-label',
  );

  if (!portalNode) {
    return null;
  }

  return ReactDOM.createPortal(
    <Box className={s.root} dataHook={DATA_HOOKS.root}>
      <div className={s.stack}>
        <div className={s.noteHost}>
          <Button
            className={classNames(s.entryChip, {
              [s.entryChipHidden]: isCardVisible,
            })}
            skin="transparent"
            priority="secondary"
            size="medium"
            dataHook={DATA_HOOKS.chip}
            tabIndex={isCardVisible ? -1 : 0}
            aria-hidden={isCardVisible}
            onClick={phase === 'collapsed' ? handleExpand : undefined}
          >
            {phase === 'analyzing' ? (
              <span className={s.entryChipContent}>
                <SparkleIcon svgClassName={s.analyzingSparkle} />
                <span className={s.shimmerText} aria-live="polite">
                  {t(
                    'premium-purchase-plan.eclipse.ai-recommendation.analyzing',
                  )}
                </span>
              </span>
            ) : (
              <span className={s.entryChipContent}>
                <SparkleIcon />
                <Text size="small" weight="bold" className={s.entryChipText}>
                  {t(
                    'premium-purchase-plan.eclipse.ai-recommendation.insight-label',
                  )}
                </Text>
              </span>
            )}
          </Button>

          {data && (
            <div
              className={classNames(s.expandedCard, {
                [s.expandedCardVisible]: isCardVisible,
              })}
              role="region"
              aria-label={ariaLabel}
              aria-hidden={!isCardVisible}
              data-hook={DATA_HOOKS.expanded}
              id={DATA_HOOKS.expanded}
            >
              <div className={s.expandedHeaderRow}>
                <div className={s.expandedHeaderLeft}>
                  <SparkleIcon />
                  <Text size="small" weight="bold" className={s.aiInsightText}>
                    {t(
                      'premium-purchase-plan.eclipse.ai-recommendation.insight-label',
                    )}
                  </Text>
                </div>
                <button
                  type="button"
                  aria-label={t(
                    'premium-purchase-plan.eclipse.ai-recommendation.close',
                  )}
                  data-hook={DATA_HOOKS.close}
                  className={s.expandedClose}
                  tabIndex={isCardVisible ? 0 : -1}
                  onClick={handleCollapse}
                >
                  <CloseIcon />
                </button>
              </div>

              <Text
                size="small"
                weight="bold"
                className={s.expandedHeading}
                dataHook={DATA_HOOKS.title}
              >
                {t(
                  'premium-purchase-plan.eclipse.ai-recommendation.recommended-plan-heading',
                )}
              </Text>

              <Text
                size="small"
                weight="thin"
                className={s.expandedSubtitle}
                dataHook={DATA_HOOKS.subtitle}
              >
                {typedSubtitle}
                {isTypingSubtitle && (
                  <span className={s.typingCursor} aria-hidden />
                )}
              </Text>

              <ul className={s.expandedBullets}>
                {bullets.slice(0, visibleBulletCount).map((bullet, index) => (
                  <li
                    key={`${index}-${bullet}`}
                    className={classNames(s.expandedBullet, s.fadeIn)}
                  >
                    <span className={s.bulletIcon} aria-hidden>
                      <Confirm width={16} height={16} />
                    </span>
                    <Text
                      size="small"
                      weight="thin"
                      className={s.expandedBulletText}
                    >
                      {bullet}
                    </Text>
                  </li>
                ))}
              </ul>

              {isComplete && (
                <div
                  className={classNames(s.expandedCta, s.fadeIn)}
                  data-hook={DATA_HOOKS.cta}
                >
                  <SelectButton size="small" />
                </div>
              )}

              <Text size="tiny" secondary className={s.disclaimer}>
                {t(
                  'premium-purchase-plan.eclipse.ai-recommendation.disclaimer',
                )}
              </Text>
            </div>
          )}
        </div>

        <button
          type="button"
          className={s.avatarContainer}
          data-hook={DATA_HOOKS.avatar}
          aria-label={ariaLabel}
          aria-expanded={isCardVisible}
          aria-controls={DATA_HOOKS.expanded}
          onClick={isCardVisible ? handleCollapse : handleExpand}
        >
          <div className={s.disk} aria-hidden />
          <div className={s.avatarFace}>
            <AriaAvatar />
          </div>
        </button>
      </div>
    </Box>,
    portalNode,
  );
};
